import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, warnQuietly } from "@/lib/api";
import { readStored, writeStored } from "./calculatorStorage";

/**
 * Work out, once on arrival, whether this visitor already has a quote on the go.
 *
 * Two ways in, and they behave differently on purpose:
 *  - a `?resume=` link from their email drops them straight back into the
 *    wizard, and the token is stripped from the URL so a refresh can't replay
 *    it;
 *  - a session handle left in this browser only *offers* to continue, via the
 *    welcome-back banner, because someone else may be sitting at the machine.
 */
export function useSessionBootstrap({ onResumed, onOffer }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [sessionId, setSessionId] = useState(null);
  const [bootstrapping, setBootstrapping] = useState(true);

  // Held in refs so the effect below can stay a genuine run-once: the handlers
  // are rebuilt on every render, and depending on them would reopen a session
  // the customer has already moved past.
  const handlers = useRef({ onResumed, onOffer });
  handlers.current = { onResumed, onOffer };

  const params = useRef({ searchParams, setSearchParams });
  params.current = { searchParams, setSearchParams };

  useEffect(() => {
    let cancelled = false;

    const fromResumeLink = async (token) => {
      const data = await api.startSession({ resume_token: token });
      if (cancelled) return;
      setSessionId(data.session_id);
      writeStored({ session_id: data.session_id });
      if (data.service && !data.completed_contact_gate) {
        handlers.current.onResumed(data);
        toast.success("Welcome back — we've picked up right where you left off.");
      }
      const next = new URLSearchParams(params.current.searchParams);
      next.delete("resume");
      params.current.setSearchParams(next, { replace: true });
    };

    const fromThisBrowser = async (storedId) => {
      const data = await api.getSession(storedId);
      if (cancelled) return;
      setSessionId(data.session_id);
      if (data.service && !data.completed_contact_gate && (data.step_index || 0) > 0) {
        handlers.current.onOffer(data);
      }
    };

    (async () => {
      const resumeToken = params.current.searchParams.get("resume");
      try {
        if (resumeToken) {
          await fromResumeLink(resumeToken);
          return;
        }
        const stored = readStored();
        if (stored?.session_id) await fromThisBrowser(stored.session_id);
      } catch (error) {
        warnQuietly("could not resume the saved session", error);
        writeStored(null);
      } finally {
        if (!cancelled) setBootstrapping(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return { sessionId, setSessionId, bootstrapping };
}
